/**
 * Who can reach one project — a port of `bimgo-ai-web`'s `ProjectMembersModal`,
 * kept to the same shape and the same wording.
 *
 * The web client's design, reproduced here rather than reinvented:
 *   - a toolbar of member search + an **Assign** button that opens a separate
 *     multi-select dialog, so several people can be added in one pass;
 *   - selection lives on the table itself, and a bar appears above it — "Selected
 *     N", Clear, and a red "Remove (N)" — rather than a per-row delete button;
 *   - the rows carry a `RoleBadge`, ordered by role and then by label, exactly as
 *     the organization members tab orders them;
 *   - a closing note that org admins and the project's creator have access
 *     regardless of what this list says.
 *
 * The rules it has to respect are not cosmetic:
 *   - **Organisation admins are shown but never selectable.** Their access is
 *     implicit in the role, and including even one in a removal batch fails the
 *     *whole* batch — the other removals go with it.
 *   - **The current user is not selectable either**, so a select-all cannot strip
 *     their own assignment and lose them the project mid-flow.
 *   - Removal is **one batched request**, not a loop, because the backend does it
 *     in a transaction.
 *   - `not_found` in the result means someone else removed that person first.
 *     Those rows are dropped too, so the table matches the server.
 *
 * Where it differs: the web client reports failures through a toast, which this
 * shell has no equivalent of, so they are shown inline in the dialog instead.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { Modal } from "../settings/Modal";
import { LoadingBlock } from "../settings/Spinner";
import { ORG_ROLES, RoleBadge, primaryRole } from "../settings/RoleBadge";
import { API_BASE_URL } from "../../lib/config";
import { getOrgMembers, type OrgMember } from "../../lib/members";
import { OrganizationsError } from "../../lib/organizations";
import {
  addProjectMember,
  bulkRemoveProjectMembers,
  getProjectMembers,
  type ProjectMember,
} from "../../lib/projectMembers";
import type { Project } from "../../lib/projects";

const ICON = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function UsersIcon() {
  return (
    <svg {...ICON} width={16} height={16}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg {...ICON} width={14} height={14}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function UserPlusIcon() {
  return (
    <svg {...ICON} width={14} height={14}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M19 8v6M22 11h-6" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg {...ICON} width={14} height={14}>
      <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg {...ICON} width={14} height={14} style={{ flex: "none", marginTop: "0.15rem" }}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg {...ICON} width={15} height={15} style={{ color: "#f59e0b", flex: "none" }}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}

/** A checkbox that can also render the tri-state dash, like Radix's. */
function TriCheckbox({
  checked,
  indeterminate = false,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  disabled?: boolean;
  label: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled ?? false}
      aria-label={label}
    />
  );
}

export function ProjectMembersModal({
  project,
  orgId,
  token,
  selfUserId,
  onClose,
}: {
  /** The project whose membership is being edited; open whenever set. */
  project: Project | null;
  orgId: string | null;
  token: string | null;
  /** Never offered for removal — see the header note. */
  selfUserId: string | null;
  onClose: () => void;
}) {
  const { t } = useLocale();

  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Assign / remove failures — the web client's toast, shown inline. */
  const [actionError, setActionError] = useState<string | null>(null);
  const [memberSearch, setMemberSearch] = useState("");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRemoving, setBulkRemoving] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignSelected, setAssignSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);

  const projectId = project?.id ?? null;

  const load = useCallback(async () => {
    if (!projectId || !orgId) return;
    setLoading(true);
    setError(null);
    try {
      const [projectRows, orgRows] = await Promise.all([
        getProjectMembers(token, projectId, { apiBaseUrl: API_BASE_URL() }),
        getOrgMembers(token, orgId, { apiBaseUrl: API_BASE_URL() }),
      ]);
      setMembers(projectRows);
      setOrgMembers(orgRows);
      // Stale ids would silently target rows the refetch just changed.
      setSelectedIds(new Set());
    } catch {
      setError(t("projectMembers.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [projectId, orgId, token, t]);

  // Fetched on every open, so the list reflects membership changed elsewhere
  // since the last time this dialog was used.
  useEffect(() => {
    if (!projectId) return;
    setMemberSearch("");
    setSelectedIds(new Set());
    setActionError(null);
    void load();
  }, [projectId, load]);

  const emailByUserId = useMemo(() => {
    const map = new Map<string, string | null>();
    orgMembers.forEach((m) => map.set(m.user_id, m.email));
    return map;
  }, [orgMembers]);

  const rolesByUserId = useMemo(() => {
    const map = new Map<string, string[]>();
    orgMembers.forEach((m) => map.set(m.user_id, m.roles));
    return map;
  }, [orgMembers]);

  // Falls back to the is_org_admin flag if the user is somehow missing from the
  // org member list (e.g. a transient load skew).
  const memberRoles = (m: ProjectMember): string[] =>
    rolesByUserId.get(m.user_id) ?? (m.is_org_admin ? ["organization_admin"] : []);

  const memberLabel = (userId: string): string =>
    emailByUserId.get(userId) || `${userId.slice(0, 8)}…`;

  const roleLabel = (role: string): string => {
    const key = `settings.org.members.role.${role}`;
    const label = t(key);
    return label === key ? role : label;
  };

  // Highest role first (org admin → project admin → advance → standard), then
  // alphabetically by label within the same role.
  const sortedMembers = useMemo(() => {
    const rank = (m: ProjectMember) => {
      const role = primaryRole(memberRoles(m));
      const index = ORG_ROLES.indexOf(role as (typeof ORG_ROLES)[number]);
      return index === -1 ? ORG_ROLES.length : index;
    };
    return [...members].sort(
      (a, b) =>
        rank(a) - rank(b) || memberLabel(a.user_id).localeCompare(memberLabel(b.user_id)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, rolesByUserId, emailByUserId]);

  // Same search behaviour as the org members tab: matches email or role label.
  const filteredMembers = useMemo(() => {
    if (!memberSearch.trim()) return sortedMembers;
    const q = memberSearch.toLowerCase();
    return sortedMembers.filter((m) => {
      if (memberLabel(m.user_id).toLowerCase().includes(q)) return true;
      return memberRoles(m).some(
        (r) => r !== "system_admin" && roleLabel(r).toLowerCase().includes(q),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedMembers, memberSearch, rolesByUserId, emailByUserId, t]);

  const isSelectable = (m: ProjectMember) => !m.is_org_admin && m.user_id !== selfUserId;

  const selectableMembers = useMemo(
    () => filteredMembers.filter(isSelectable),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredMembers, selfUserId],
  );

  // The selection Set may hold ids the search box currently hides. Everything
  // user-facing (the count, the request payload) derives from the *visible*
  // intersection, so "Remove (N)" can never act on a row the user cannot see —
  // while narrowing the search does not discard what they already ticked.
  const selectedMembers = useMemo(
    () => selectableMembers.filter((m) => selectedIds.has(m.user_id)),
    [selectableMembers, selectedIds],
  );

  const allVisibleSelected =
    selectableMembers.length > 0 && selectedMembers.length === selectableMembers.length;

  const toggleSelected = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  // Select-all operates on the filtered rows — what you see is what you select.
  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) selectableMembers.forEach((m) => next.delete(m.user_id));
      else selectableMembers.forEach((m) => next.add(m.user_id));
      return next;
    });
  };

  const assignableMembers = useMemo(() => {
    const assigned = new Set(members.map((m) => m.user_id));
    // Org admins are excluded twice over: they come back in the members list
    // (is_org_admin), and their role makes them unassignable anyway (the API
    // 409s — their access is implicit).
    return orgMembers.filter(
      (m) => !assigned.has(m.user_id) && !m.roles.includes("organization_admin"),
    );
  }, [members, orgMembers]);

  const dialogMembers = useMemo(() => {
    if (!assignSearch.trim()) return assignableMembers;
    const q = assignSearch.toLowerCase();
    return assignableMembers.filter((m) => (m.email || m.user_id).toLowerCase().includes(q));
  }, [assignableMembers, assignSearch]);

  const toggleAssignSelected = (userId: string) => {
    setAssignSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const closeAssign = () => {
    setAssignOpen(false);
    setAssignSearch("");
    setAssignSelected(new Set());
  };

  async function handleAssign() {
    if (!projectId || assignSelected.size === 0 || adding) return;
    setAdding(true);
    setActionError(null);
    const added: ProjectMember[] = [];
    const failed: string[] = [];
    for (const userId of Array.from(assignSelected)) {
      try {
        const row = await addProjectMember(token, projectId, userId, {
          apiBaseUrl: API_BASE_URL(),
        });
        if (row) added.push(row);
      } catch {
        failed.push(userId);
      }
    }
    if (added.length) setMembers((prev) => [...prev, ...added]);
    setAdding(false);
    if (failed.length) {
      // Keep the dialog open with only the failures still ticked.
      setAssignSelected(new Set(failed));
      setActionError(t("projectMembers.addFailed"));
    } else {
      closeAssign();
    }
  }

  async function handleBulkRemove() {
    const userIds = selectedMembers.map((m) => m.user_id);
    if (!projectId || userIds.length === 0 || bulkRemoving) return;
    setBulkRemoving(true);
    setActionError(null);
    try {
      const result = await bulkRemoveProjectMembers(token, projectId, userIds, {
        apiBaseUrl: API_BASE_URL(),
      });
      // not_found rows were already gone server-side (removed concurrently by
      // another owner) — drop them too so the table matches the server.
      const gone = new Set([...result.removed, ...result.not_found]);
      setMembers((prev) => prev.filter((m) => !gone.has(m.user_id)));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        gone.forEach((id) => next.delete(id));
        return next;
      });
    } catch (e) {
      // 409 = the batch contained an org admin (promoted mid-flow — the UI
      // never offers them). Nothing was removed; the selection is kept so the
      // user can retry.
      const status =
        e instanceof OrganizationsError && e.failure.kind === "http" ? e.failure.status : null;
      setActionError(
        status === 409
          ? t("projectMembers.removeOrgAdminBlocked")
          : t("projectMembers.bulkRemoveFailed"),
      );
    } finally {
      setBulkRemoving(false);
    }
  }

  return (
    <>
      <Modal
        open={project !== null && !confirmRemoveOpen && !assignOpen}
        onClose={() => (bulkRemoving ? undefined : onClose())}
        title={t("projectMembers.title")}
        description={project?.name ?? undefined}
        icon={
          <span className="modal-icon-glyph">
            <UsersIcon />
          </span>
        }
        wide
      >
        {loading ? (
          <LoadingBlock />
        ) : error ? (
          <p className="settings-error-row">
            <AlertIcon />
            <span>{error}</span>
          </p>
        ) : (
          <>
            <div className="member-toolbar">
              <div className="search-field">
                <span className="search-field-icon">
                  <SearchIcon />
                </span>
                <input
                  className="settings-input"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder={t("settings.org.members.searchPlaceholder")}
                />
              </div>
              <button
                type="button"
                className="settings-btn"
                onClick={() => setAssignOpen(true)}
                disabled={assignableMembers.length === 0}
                title={
                  assignableMembers.length === 0 ? t("projectMembers.allAssigned") : undefined
                }
              >
                <UserPlusIcon />
                {t("projectMembers.add")}
              </button>
            </div>

            {/* Sits above the table rather than in a footer, so it stays
                visible with the list's own scroll area. */}
            {selectedMembers.length > 0 ? (
              <div className="bulk-bar">
                <span>{`${t("projectMembers.selectedCount")} ${selectedMembers.length}`}</span>
                <div className="bulk-bar-actions">
                  <button
                    type="button"
                    className="settings-btn ghost"
                    onClick={() => setSelectedIds(new Set())}
                    disabled={bulkRemoving}
                  >
                    {t("projectMembers.clearSelection")}
                  </button>
                  <button
                    type="button"
                    className="settings-btn is-danger"
                    onClick={() => setConfirmRemoveOpen(true)}
                    disabled={bulkRemoving}
                  >
                    <TrashIcon />
                    {`${t("projectMembers.removeSelected")} (${selectedMembers.length})`}
                  </button>
                </div>
              </div>
            ) : null}

            {members.length === 0 ? (
              <p className="settings-note is-centered">{t("projectMembers.empty")}</p>
            ) : filteredMembers.length === 0 ? (
              <p className="settings-note is-centered">{t("projectMembers.noMemberFound")}</p>
            ) : (
              <div className="member-scroll">
                <table className="members-table">
                  <thead>
                    <tr>
                      <th className="col-check">
                        <TriCheckbox
                          checked={allVisibleSelected}
                          indeterminate={selectedMembers.length > 0}
                          onChange={toggleSelectAllVisible}
                          disabled={bulkRemoving || selectableMembers.length === 0}
                          label={t("projectMembers.selectAll")}
                        />
                      </th>
                      <th>{t("settings.org.members.colMember")}</th>
                      <th>{t("settings.org.members.colRoles")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map((m) => (
                      <tr key={m.user_id}>
                        <td className="col-check">
                          {/* Empty for org admins and the current user; the
                              cell stays so the columns line up. */}
                          {isSelectable(m) ? (
                            <TriCheckbox
                              checked={selectedIds.has(m.user_id)}
                              onChange={() => toggleSelected(m.user_id)}
                              disabled={bulkRemoving}
                              label={t("projectMembers.selectMember")}
                            />
                          ) : null}
                        </td>
                        <td className="member-email">{memberLabel(m.user_id)}</td>
                        <td>
                          <RoleBadge roles={memberRoles(m)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {actionError ? (
              <p className="settings-msg err" role="alert">
                {actionError}
              </p>
            ) : null}

            <p className="member-note">
              <InfoIcon />
              <span>{t("projectMembers.implicitAccessNote")}</span>
            </p>
          </>
        )}
      </Modal>

      {/* A sibling of the members dialog, not a child — closing it must not
          take the members view with it. */}
      <Modal
        open={assignOpen}
        onClose={() => (adding ? undefined : closeAssign())}
        title={t("projectMembers.assignTitle")}
        description={project?.name ?? undefined}
        footer={
          <>
            <button
              type="button"
              className="settings-btn secondary"
              onClick={closeAssign}
              disabled={adding}
            >
              {t("projects.cancel")}
            </button>
            <button
              type="button"
              className="settings-btn"
              onClick={() => void handleAssign()}
              disabled={assignSelected.size === 0 || adding}
            >
              <UserPlusIcon />
              {t("projectMembers.add")}
              {assignSelected.size > 0 ? ` (${assignSelected.size})` : ""}
            </button>
          </>
        }
      >
        <div className="search-field">
          <span className="search-field-icon">
            <SearchIcon />
          </span>
          <input
            className="settings-input"
            value={assignSearch}
            onChange={(e) => setAssignSearch(e.target.value)}
            placeholder={t("projectMembers.searchMember")}
          />
        </div>

        {dialogMembers.length === 0 ? (
          <p className="settings-note is-centered">
            {assignableMembers.length === 0
              ? t("projectMembers.allAssigned")
              : t("projectMembers.noMemberFound")}
          </p>
        ) : (
          <ul className="assign-list">
            {dialogMembers.map((m) => (
              <li key={m.user_id}>
                <label className="assign-row">
                  <input
                    type="checkbox"
                    checked={assignSelected.has(m.user_id)}
                    onChange={() => toggleAssignSelected(m.user_id)}
                    disabled={adding}
                  />
                  <span className="assign-name">{m.email || `${m.user_id.slice(0, 8)}…`}</span>
                  <RoleBadge roles={m.roles} />
                </label>
              </li>
            ))}
          </ul>
        )}

        {actionError ? (
          <p className="settings-msg err" role="alert">
            {actionError}
          </p>
        ) : null}
      </Modal>

      {/* Also a sibling, so the confirmation is not nested inside the dialog it
          is confirming. `t()` has no count interpolation here, hence the
          prefix/suffix pair around the number — the web client does the same. */}
      <Modal
        open={confirmRemoveOpen}
        onClose={() => setConfirmRemoveOpen(false)}
        title={t("projectMembers.removeConfirmTitle")}
        footer={
          <>
            <button
              type="button"
              className="settings-btn secondary"
              onClick={() => setConfirmRemoveOpen(false)}
            >
              {t("projects.cancel")}
            </button>
            <button
              type="button"
              className="settings-btn is-danger"
              onClick={() => {
                setConfirmRemoveOpen(false);
                void handleBulkRemove();
              }}
            >
              <TrashIcon />
              {t("projectMembers.removeSelected")}
            </button>
          </>
        }
      >
        <p className="danger-warning">
          {`${t("projectMembers.removeConfirmDescPrefix")} ${selectedMembers.length} ${t("projectMembers.removeConfirmDescSuffix")}`}
        </p>
      </Modal>
    </>
  );
}
